import React, { FC, useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { Box, Flex, Text } from '@chakra-ui/react';

const F = { ui:"'Rajdhani','Arial',sans-serif", mono:"'Share Tech Mono',monospace",
  thermal:"'Courier New',Courier,monospace" };

interface Player {
  id: string; name: string; buyIn: number; rebuys: number;
  addOns: number; seat?: number; registeredAt: number;
}

interface SeatCardPrintProps {
  eventId: string;
  eventName: string;
  venueName?: string;
  buyIn: number;
  rebuyAllowed?: boolean;
  addOnAllowed?: boolean;
  lateRegLevel?: number;
  players: Player[];
  onClose: () => void;
}

const SeatCard: FC<{
  player: Player; eventName: string; venueName?: string;
  buyIn: number; rebuyAllowed?: boolean; addOnAllowed?: boolean;
  lateRegLevel?: number; eventDate: string;
}> = ({ player, eventName, venueName, buyIn, rebuyAllowed, addOnAllowed, lateRegLevel, eventDate }) => (
  <Box style={{
    width: '72mm', minHeight: '55mm',
    background: '#FFFFFF', color: '#000000',
    fontFamily: F.thermal, fontSize: '10px',
    border: '1px dashed #999', borderRadius: '4px',
    padding: '6px 8px', margin: '4px',
    pageBreakInside: 'avoid',
    display: 'inline-block', verticalAlign: 'top',
  }}>
    {/* Header */}
    <div style={{ textAlign:'center', borderBottom:'1px solid #000',
      paddingBottom:3, marginBottom:4 }}>
      <div style={{ fontFamily:F.mono, fontSize:9, fontWeight:'bold',
        letterSpacing:'0.15em' }}>pokercity ♣</div>
      <div style={{ fontFamily:F.thermal, fontSize:10, fontWeight:'bold',
        lineHeight:1.2, marginTop:2 }}>
        {eventName.length > 28 ? eventName.substring(0,28)+'…' : eventName}
      </div>
      {venueName && (
        <div style={{ fontSize:8, color:'#444', marginTop:1 }}>{venueName}</div>
      )}
      <div style={{ fontSize:8, color:'#666', marginTop:1 }}>{eventDate}</div>
    </div>

    {/* Player info */}
    <div style={{ marginBottom:4 }}>
      <div style={{ fontSize:13, fontWeight:'bold', lineHeight:1.1 }}>
        {player.name.length > 22 ? player.name.substring(0,22)+'…' : player.name}
      </div>
      {player.seat && (
        <div style={{ fontSize:10, color:'#333', marginTop:1 }}>
          Seat: {player.seat}
        </div>
      )}
    </div>

    {/* Buy-in info */}
    <div style={{ borderTop:'1px dotted #ccc', paddingTop:3, marginBottom:4 }}>
      <div style={{ display:'flex', justifyContent:'space-between' }}>
        <span style={{ fontSize:9 }}>Buy-in:</span>
        <span style={{ fontSize:10, fontWeight:'bold' }}>${buyIn}.00</span>
      </div>
      {lateRegLevel && (
        <div style={{ fontSize:8, color:'#666', marginTop:1 }}>
          Late reg until Level {lateRegLevel}
        </div>
      )}
    </div>

    {/* Rebuy/Add-on boxes */}
    {(rebuyAllowed || addOnAllowed) && (
      <div style={{ borderTop:'1px dotted #ccc', paddingTop:3 }}>
        <div style={{ fontSize:8, color:'#666', marginBottom:3 }}>
          Mark when taken:
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {rebuyAllowed && (
            <div>
              <div style={{ fontSize:8, color:'#333', marginBottom:2 }}>REBUY</div>
              <div style={{ display:'flex', gap:3 }}>
                {[1,2,3].map(n => (
                  <div key={n} style={{ width:16, height:16, border:'1px solid #000',
                    borderRadius:2, display:'flex', alignItems:'center',
                    justifyContent:'center', fontSize:8, color:'#999' }}>{n}</div>
                ))}
              </div>
            </div>
          )}
          {addOnAllowed && (
            <div>
              <div style={{ fontSize:8, color:'#333', marginBottom:2 }}>ADD-ON</div>
              <div style={{ width:16, height:16, border:'1px solid #000',
                borderRadius:2 }} />
            </div>
          )}
        </div>
      </div>
    )}

    {/* QR placeholder line */}
    <div style={{ marginTop:4, borderTop:'1px dotted #ccc', paddingTop:3,
      textAlign:'center', fontSize:7, color:'#999' }}>
      [QR: pokercity.com/dealer/{player.id.substring(0,8)}]
    </div>
  </Box>
);

const SeatCardPrint: FC<SeatCardPrintProps> = ({
  eventId, eventName, venueName, buyIn, rebuyAllowed, addOnAllowed,
  lateRegLevel, players, onClose
}) => {
  const [printing, setPrinting] = useState(false);
  const eventDate = new Date().toLocaleDateString('en-CA', {
    weekday:'short', month:'short', day:'numeric', year:'numeric'
  });

  const handlePrint = () => {
    setPrinting(true);
    setTimeout(() => { window.print(); setPrinting(false); }, 200);
  };

  return (
    <Box position="fixed" inset={0} bg="rgba(0,0,0,0.9)" zIndex={2000}
      display="flex" flexDirection="column" overflow="hidden">

      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #seat-cards-print, #seat-cards-print * { visibility: visible !important; }
          #seat-cards-print {
            position: fixed !important; top: 0 !important; left: 0 !important;
            width: 100% !important; background: white !important;
            padding: 8mm !important;
          }
          @page { margin: 8mm; size: A4 portrait; }
        }
      `}</style>

      {/* Controls */}
      <Flex bg="#131720" px={4} py={3} justify="space-between" align="center"
        flexShrink={0} borderBottom="1px solid #1E2330">
        <Box>
          <Text style={{ fontFamily:F.ui, fontSize:16, fontWeight:800,
            color:'#F0F4FF', marginBottom:2 }}>
            🖨 Seat Cards — {players.length} players
          </Text>
          <Text style={{ fontFamily:F.ui, fontSize:12, color:'#6B7590' }}>
            Print these on a thermal printer or standard printer.
            72mm × 55mm per card.
          </Text>
        </Box>
        <Flex gap={3}>
          <Box as="button" px={5} py={2} bg="#052E16"
            border="1px solid #166534" borderRadius={6}
            fontSize="14px" fontWeight="700" color="#86EFAC"
            fontFamily={F.ui} cursor={printing?'wait':'pointer'}
            onClick={handlePrint}>
            {printing ? 'Opening...' : '🖨 Print All Cards'}
          </Box>
          <Box as="button" px={4} py={2} bg="#131720"
            border="1px solid #1E2330" borderRadius={6}
            fontSize="13px" color="#6B7590"
            fontFamily={F.ui} cursor="pointer"
            onClick={onClose}>✕ Close</Box>
        </Flex>
      </Flex>

      {/* Preview area */}
      <Box flex={1} overflowY="auto" p={4} bg="#0A0A0A">
        <div id="seat-cards-print" style={{ background:'white', padding:8 }}>
          {players.length === 0 ? (
            <Box p={8} textAlign="center">
              <Text style={{ fontFamily:F.ui, fontSize:16, color:'#6B7590' }}>
                No players registered yet. Add players in the PCTDS Clock first.
              </Text>
            </Box>
          ) : (
            <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
              {players.map((p, i) => (
                <SeatCard key={p.id}
                  player={{ ...p, seat: p.seat || i+1 }}
                  eventName={eventName}
                  venueName={venueName}
                  buyIn={buyIn}
                  rebuyAllowed={rebuyAllowed}
                  addOnAllowed={addOnAllowed}
                  lateRegLevel={lateRegLevel}
                  eventDate={eventDate}
                />
              ))}
            </div>
          )}
        </div>
      </Box>

      {/* Tip bar */}
      <Box bg="#0D1520" px={4} py={2} borderTop="1px solid #1E2330" flexShrink={0}>
        <Text style={{ fontFamily:F.ui, fontSize:12, color:'#6B7590' }}>
          💡 Tip: Set printer margins to minimum (4–6mm) and paper size to A4 or Letter.
          For thermal printer, set roll width to 72mm and cut between cards.
          Cards fit 3 per row on A4.
        </Text>
      </Box>
    </Box>
  );
};

export default SeatCardPrint;
